import React, { useState, useEffect } from 'react';
import { ShoppingCart, Eye, Search, RefreshCw, Calendar, User, Package } from 'lucide-react';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { OrdersDto, OrderLineDto } from '../../../../../types/dataInspection';
import { BackButton } from '../../../../common/BackButton';

interface OrdersViewerProps {
    onBack: () => void;
}

export const OrdersViewer: React.FC<OrdersViewerProps> = ({ onBack }) => {
    const [orders, setOrders] = useState<OrdersDto[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<OrdersDto | null>(null);
    const [orderLines, setOrderLines] = useState<OrderLineDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getAllOrders();
            setOrders(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const loadOrderLines = async (orderNo: number) => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getOrderWithLines(orderNo);
            setSelectedOrder(data.header);
            setOrderLines(data.lines);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const handleViewOrder = (order: OrdersDto) => {
        loadOrderLines(order.orderNo);
    };

    const handleBackToList = () => {
        setSelectedOrder(null);
        setOrderLines([]);
    };

    const filteredOrders = orders.filter(order => 
        order.orderNo.toString().includes(searchTerm) ||
        order.customerNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.orderNoERP?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status: number) => {
        switch (status) {
            case 0: return 'bg-gray-100 text-gray-800';
            case 1: return 'bg-blue-100 text-blue-800';
            case 2: return 'bg-yellow-100 text-yellow-800';
            case 3: return 'bg-green-100 text-green-800';
            case 4: return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: number) => {
        switch (status) {
            case 0: return 'Ny';
            case 1: return 'Bekreftet';
            case 2: return 'Under behandling';
            case 3: return 'Levert';
            case 4: return 'Kansellert';
            default: return `Status ${status}`;
        }
    };

    if (selectedOrder) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <BackButton
                        onClick={handleBackToList}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                        iconSize={16}
                        label="Tilbake til liste"
                    />
                    <div className="flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-green-600" />
                        <h1 className="text-xl font-semibold">Salgsordre {selectedOrder.orderNo}</h1>
                    </div>
                </div>

                {/* Order Header Info */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-500">Ordre Nr.</label>
                            <div className="text-lg font-semibold text-gray-900">{selectedOrder.orderNo}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">ERP Nr.</label>
                            <div className="text-gray-900">{selectedOrder.orderNoERP || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Status</label>
                            <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.orderStatus)}`}>
                                {getStatusText(selectedOrder.orderStatus)}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Kunde Nr.</label>
                            <div className="text-gray-900">{selectedOrder.customerNo}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Kundenavn</label>
                            <div className="text-gray-900">{selectedOrder.customerName || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Ordredato</label>
                            <div className="text-gray-900">
                                {selectedOrder.orderDate ? new Date(selectedOrder.orderDate).toLocaleDateString('nb-NO') : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Leveringsdato</label>
                            <div className="text-gray-900">
                                {selectedOrder.deliveryDate ? new Date(selectedOrder.deliveryDate).toLocaleDateString('nb-NO') : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Lager</label>
                            <div className="text-gray-900">{selectedOrder.warehouse || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Leveringsmetode</label>
                            <div className="text-gray-900">{selectedOrder.deliveryMethod || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                {/* Delivery Address */}
                {(selectedOrder.delCustomerName || selectedOrder.delAddress1) && (
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Leveringsadresse</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-500">Navn</label>
                                <div className="text-gray-900">{selectedOrder.delCustomerName || 'N/A'}</div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-500">Adresse</label>
                                <div className="text-gray-900">
                                    {selectedOrder.delAddress1 && <div>{selectedOrder.delAddress1}</div>}
                                    {selectedOrder.delAddress2 && <div>{selectedOrder.delAddress2}</div>}
                                    {selectedOrder.delZipCode && selectedOrder.delCity && (
                                        <div>{selectedOrder.delZipCode} {selectedOrder.delCity}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Order Lines */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Orderlinjer ({orderLines.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linje</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vare</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beskrivelse</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mengde</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Levert</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enhet</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pris</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {orderLines.map(line => (
                                    <tr key={line.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {line.lineNo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.itemNo}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {line.description1}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.quantity}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.quantityDelivered || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.itemUnitCode}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.price ? `${line.price.toFixed(2)} kr` : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(line.lineStatus)}`}>
                                                {getStatusText(line.lineStatus)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <BackButton
                    onClick={onBack}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    iconSize={16}
                    label="Tilbake til grunndata"
                />
                <div className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-green-600" />
                    <h1 className="text-xl font-semibold">Salgsordrer</h1>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Søk etter ordre nr., kunde eller ERP nr..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <button
                        onClick={loadOrders}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Oppdater
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="font-medium">Feil:</span>
                    </div>
                    <p className="text-red-600 mt-1">{error}</p>
                </div>
            )}

            {/* Orders Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Salgsordrer ({filteredOrders.length})</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordre Nr.</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ERP Nr.</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kunde</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kundenavn</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordredato</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leveringsdato</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handlinger</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredOrders.map(order => (
                                <tr key={order.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {order.orderNo}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {order.orderNoERP || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                                            {getStatusText(order.orderStatus)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {order.customerNo}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {order.customerName || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {order.orderDate ? new Date(order.orderDate).toLocaleDateString('nb-NO') : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString('nb-NO') : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => handleViewOrder(order)}
                                            className="flex items-center gap-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                            Vis detaljer
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};